/**
 * NavHistory — lightweight in-app navigation stack backed by sessionStorage.
 *
 * Why sessionStorage: persists across React re-renders and Suspense loads,
 * survives soft refreshes within the same tab, clears automatically when the
 * tab is closed. Does not leak across tabs.
 *
 * The stack tracks pathnames + search so query-param changes are captured.
 * Back navigation skips any entry that matches FORM_PATTERNS so users never
 * land back on a create/edit form after saving.
 */

const STACK_KEY = "kv.nav_stack";
const MAX_SIZE  = 50;

/** Routes that should be skipped when navigating backwards. */
const FORM_PATTERNS = [
  /\/new(\?|$)/,
  /\/edit(\/|$)/,
  /\/create(\?|$)/,
  /\/setup(\?|$)/,
  /\/wizard(\?|$)/,
  /\/onboarding(\?|$)/,
];

function isFormRoute(path) {
  return FORM_PATTERNS.some((r) => r.test(path));
}

function readStack() {
  try {
    return JSON.parse(sessionStorage.getItem(STACK_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeStack(s) {
  try {
    sessionStorage.setItem(STACK_KEY, JSON.stringify(s));
  } catch {}
}

export const NavHistory = {
  /**
   * Push a new path onto the stack.
   * Consecutive duplicates are silently ignored.
   */
  push(path) {
    const s = readStack();
    if (s.length > 0 && s[s.length - 1] === path) return;
    s.push(path);
    if (s.length > MAX_SIZE) s.shift();
    writeStack(s);
  },

  /**
   * Pop the current entry and return the path to navigate to.
   * Skips any intermediate form routes.
   * Falls back to `safeFallback` when the stack is exhausted.
   */
  back(safeFallback = "/") {
    const s = readStack();
    // Remove current page
    s.pop();
    // Skip any form pages that precede the current entry
    while (s.length > 0 && isFormRoute(s[s.length - 1])) {
      s.pop();
    }
    const target = s.length > 0 ? s[s.length - 1] : safeFallback;
    writeStack(s);
    return target;
  },

  /**
   * Peek at where back() would navigate without consuming the stack.
   * Useful for conditional UI (e.g., showing a custom label on the back button).
   */
  peek(safeFallback = "/") {
    const s = readStack();
    if (s.length < 2) return safeFallback;
    let i = s.length - 2;
    while (i >= 0 && isFormRoute(s[i])) i--;
    return i >= 0 ? s[i] : safeFallback;
  },

  /**
   * Remove all occurrences of a specific path from the stack.
   * Call before programmatic post-delete navigation so back() can never
   * return to a deleted entity's URL.
   */
  dropPath(path) {
    const s = readStack().filter((entry) => entry !== path);
    writeStack(s);
  },

  /** Reset the stack — call on logout. */
  clear() {
    try {
      sessionStorage.removeItem(STACK_KEY);
    } catch {}
  },
};
